/**
 * Startup validation of the VITE_SUPABASE_* env vars that the generated
 * Supabase client depends on. Vite inlines these at build time, so if any are
 * missing or point at the wrong project the published app breaks with no
 * useful message (Supabase requests just go to `undefined`). This module
 * surfaces those problems loudly at boot so the developer can fix `.env`
 * before shipping.
 */

export type EnvIssueSeverity = "error" | "warning";

export interface EnvIssue {
  variable: string;
  severity: EnvIssueSeverity;
  message: string;
}

export interface EnvCheckResult {
  ok: boolean;
  hasErrors: boolean;
  issues: EnvIssue[];
  values: {
    url?: string;
    publishableKey?: string;
    projectId?: string;
    urlRef?: string;
    keyRef?: string;
  };
}

const SUPABASE_URL_RE = /^https:\/\/([a-z0-9]{20,})\.supabase\.co\/?$/i;
const PROJECT_REF_RE = /^[a-z0-9]{20,}$/i;
const JWT_RE = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;

/** Base64URL decode → UTF-8 string. Used to peek at the JWT payload. */
function decodeBase64Url(input: string): string | null {
  try {
    const b64 = input.replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
    if (typeof atob === "function") return atob(padded);
    // Node fallback for tests.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const g = globalThis as any;
    if (typeof g.Buffer !== "undefined") {
      return g.Buffer.from(padded, "base64").toString("utf-8");
    }
    return null;
  } catch {
    return null;
  }
}

function parseJwtRef(token: string): { ref?: string; role?: string } | null {
  if (!JWT_RE.test(token)) return null;
  const payload = decodeBase64Url(token.split(".")[1] ?? "");
  if (!payload) return null;
  try {
    const parsed = JSON.parse(payload) as { ref?: string; role?: string };
    return { ref: parsed.ref, role: parsed.role };
  } catch {
    return null;
  }
}

/**
 * Validate the current Vite env vars.
 *
 * Accepts an optional env object so this can be unit-tested without touching
 * `import.meta.env`.
 */
export function checkSupabaseEnv(
  env: Record<string, string | undefined> = readViteEnv(),
): EnvCheckResult {
  const issues: EnvIssue[] = [];
  const url = env.VITE_SUPABASE_URL?.trim();
  const publishableKey = env.VITE_SUPABASE_PUBLISHABLE_KEY?.trim();
  const projectId = env.VITE_SUPABASE_PROJECT_ID?.trim();

  let urlRef: string | undefined;
  let keyRef: string | undefined;

  // --- VITE_SUPABASE_URL ------------------------------------------------
  if (!url) {
    issues.push({
      variable: "VITE_SUPABASE_URL",
      severity: "error",
      message: "Missing. Expected something like https://<project-ref>.supabase.co.",
    });
  } else {
    const match = url.match(SUPABASE_URL_RE);
    if (!match) {
      issues.push({
        variable: "VITE_SUPABASE_URL",
        severity: "error",
        message:
          "Does not look like a Supabase project URL. Expected https://<project-ref>.supabase.co.",
      });
    } else {
      urlRef = match[1].toLowerCase();
    }
  }

  // --- VITE_SUPABASE_PUBLISHABLE_KEY ------------------------------------
  if (!publishableKey) {
    issues.push({
      variable: "VITE_SUPABASE_PUBLISHABLE_KEY",
      severity: "error",
      message: "Missing. This is the anon/publishable JWT for your Supabase project.",
    });
  } else if (!JWT_RE.test(publishableKey)) {
    issues.push({
      variable: "VITE_SUPABASE_PUBLISHABLE_KEY",
      severity: "error",
      message:
        "Does not look like a JWT (expected three base64url segments separated by dots). Copy the anon/publishable key from the project's API settings.",
    });
  } else {
    const claims = parseJwtRef(publishableKey);
    if (!claims) {
      issues.push({
        variable: "VITE_SUPABASE_PUBLISHABLE_KEY",
        severity: "warning",
        message: "Could not decode the JWT payload — skipping project-ref cross-check.",
      });
    } else {
      keyRef = claims.ref?.toLowerCase();
      if (claims.role && claims.role !== "anon") {
        issues.push({
          variable: "VITE_SUPABASE_PUBLISHABLE_KEY",
          severity: "error",
          message: `JWT role is "${claims.role}", not "anon". Never ship a service_role key to the browser.`,
        });
      }
      if (!keyRef) {
        issues.push({
          variable: "VITE_SUPABASE_PUBLISHABLE_KEY",
          severity: "warning",
          message: "JWT is missing a project ref claim; cannot verify it matches VITE_SUPABASE_URL.",
        });
      }
    }
  }

  // --- VITE_SUPABASE_PROJECT_ID -----------------------------------------
  if (projectId && !PROJECT_REF_RE.test(projectId)) {
    issues.push({
      variable: "VITE_SUPABASE_PROJECT_ID",
      severity: "warning",
      message: "Doesn't look like a Supabase project ref (lowercase alphanumerics, 20+ chars).",
    });
  }

  // --- Cross-variable consistency ---------------------------------------
  if (urlRef && keyRef && urlRef !== keyRef) {
    issues.push({
      variable: "VITE_SUPABASE_URL / VITE_SUPABASE_PUBLISHABLE_KEY",
      severity: "error",
      message: `URL points at project "${urlRef}" but the publishable key was issued for "${keyRef}". Requests will fail with 401/403.`,
    });
  }
  if (urlRef && projectId && urlRef !== projectId.toLowerCase()) {
    issues.push({
      variable: "VITE_SUPABASE_URL / VITE_SUPABASE_PROJECT_ID",
      severity: "error",
      message: `URL points at project "${urlRef}" but VITE_SUPABASE_PROJECT_ID is "${projectId}".`,
    });
  }
  if (keyRef && projectId && keyRef !== projectId.toLowerCase()) {
    issues.push({
      variable: "VITE_SUPABASE_PUBLISHABLE_KEY / VITE_SUPABASE_PROJECT_ID",
      severity: "error",
      message: `Publishable key was issued for "${keyRef}" but VITE_SUPABASE_PROJECT_ID is "${projectId}".`,
    });
  }

  const hasErrors = issues.some((i) => i.severity === "error");
  return {
    ok: issues.length === 0,
    hasErrors,
    issues,
    values: { url, publishableKey, projectId, urlRef, keyRef },
  };
}

function readViteEnv(): Record<string, string | undefined> {
  try {
    // IMPORTANT: use direct static `import.meta.env.VITE_X` accesses so Vite
    // can inline the values at transform time. A dynamic read like
    // `(import.meta as any)?.env` is NOT replaced by Vite and comes back
    // undefined at runtime, causing false "missing variable" errors.
    return {
      VITE_SUPABASE_URL: import.meta.env.VITE_SUPABASE_URL,
      VITE_SUPABASE_PUBLISHABLE_KEY: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
      VITE_SUPABASE_PROJECT_ID: import.meta.env.VITE_SUPABASE_PROJECT_ID,
    };
  } catch {
    return {};
  }
}
